import { useState, useEffect, useRef } from 'react'
import { supabase } from '../../services/supabase'
import { useAdvisors } from '../../hooks/useAdvisors'
import { useCurrentAdvisor } from '../../hooks/useCurrentAdvisor'
import { useTaskSections } from '../../hooks/useTaskSections'
import {
  X, MoreHorizontal, CheckCircle, RotateCcw, UserCheck,
  Paperclip, Download, FileText, Send, ChevronDown, ChevronUp,
  Calendar, CalendarClock, User, Flag, UserPlus, Trash2, Mic, MicOff,
  ChevronLeft, ChevronRight, GitBranch,
} from 'lucide-react'
import { AdvisorAvatar } from '../../components/Common/AdvisorAvatar'
import { VoicePlayer } from '../../components/Tasks/VoicePlayer'
import { SECTION_COLORS } from '../../components/Tasks/TaskCard'
import { CreateTaskModal } from '../../components/Tasks/CreateTaskModal'

// ── Claim stages ────────────────────────────────────────────────────────────
const CLAIM_STAGES = [
  { key: 'received',      label: 'Received',          color: 'bg-gray-400' },
  { key: 'registered',    label: 'Registered',        color: 'bg-blue-500' },
  { key: 'assessment',    label: 'Under Assessment',  color: 'bg-yellow-500' },
  { key: 'awaiting_docs', label: 'Awaiting Documents',color: 'bg-orange-500' },
  { key: 'decision',      label: 'Decision Pending',  color: 'bg-purple-500' },
  { key: 'settled',       label: 'Settled',           color: 'bg-green-500' },
  { key: 'declined',      label: 'Declined',          color: 'bg-red-500' },
]

// ── Common emoji set ─────────────────────────────────────────────────────────
const COMMON_EMOJI = [
  '👍','❤️','😂','😮','😢','👏','🎉','🔥','✅','❌',
  '⚠️','📎','📋','💬','🔔','✏️','📌','🏷️','🗂️','📁',
  '🔍','💡','🛠️','📞','📧','🤝','👀','⏰','📅','🔗',
  '😊','🙏','💪','🎯','📊','✔️','🚀','💯','👋','🫡',
]

const STATUS_COLORS = {
  open:        'bg-gray-100 text-gray-700',
  in_progress: 'bg-blue-100 text-blue-700',
  completed:   'bg-green-100 text-green-700',
  cancelled:   'bg-red-100 text-red-700',
}

export function TaskDetailPanel({ taskId, isDarkMode, onClose, onTaskUpdated, onTaskDeleted, onOpenTask }) {
  const { advisor: currentAdvisor } = useCurrentAdvisor()
  const { advisors } = useAdvisors()
  useTaskSections()

  const [task, setTask] = useState(null)
  const [attachments, setAttachments] = useState([])
  const [comments, setComments] = useState([])
  const [reassignments, setReassignments] = useState([])
  const [coAdvisors, setCoAdvisors] = useState([])     // [{advisor_id, advisors:{id,full_name,avatar_url}}]
  const [loading, setLoading] = useState(true)
  const [activeTab, setActiveTab] = useState('comments')

  // Emoji reactions
  const [reactionsMap, setReactionsMap] = useState({})     // { [commentId]: [{advisor_id, emoji}] }
  const [reactionPickerFor, setReactionPickerFor] = useState(null) // commentId whose picker is open

  // Action menu
  const [menuOpen, setMenuOpen] = useState(false)
  const menuRef = useRef(null)

  // Action panels
  const [actionPanel, setActionPanel] = useState(null) // 'complete'|'reopen'|'reassign'|'delete'|'extend_due'
  const [deleteReason, setDeleteReason] = useState('')
  const [completionNotes, setCompletionNotes] = useState('')
  const [newFiles, setNewFiles] = useState([])
  const [reopenReason, setReopenReason] = useState('')
  const [reassignToId, setReassignToId] = useState('')
  const [reassignReason, setReassignReason] = useState('')
  const [extendNewDate, setExtendNewDate] = useState('')
  const [extendReason, setExtendReason] = useState('')
  const [saving, setSaving] = useState(false)
  const [actionError, setActionError] = useState(null)

  // Comment + @mention
  const [commentBody, setCommentBody] = useState('')
  const [sendingComment, setSendingComment] = useState(false)
  const [mentionOpen, setMentionOpen] = useState(false)
  const [mentionSearch, setMentionSearch] = useState('')
  const commentsEndRef = useRef(null)
  const commentInputRef = useRef(null)

  // Emoji picker
  const [emojiOpen, setEmojiOpen] = useState(false)

  // File upload in chat
  const chatFileRef = useRef(null)
  const [uploadingFile, setUploadingFile] = useState(false)

  // Media lightbox
  const [expandedMedia, setExpandedMedia] = useState(null) // { url, mime, name }

  // Voice note
  const [recording, setRecording] = useState(false)
  const [mediaRecorder, setMediaRecorder] = useState(null)
  const audioChunksRef = useRef([])
  const [uploadingVoice, setUploadingVoice] = useState(false)
  const [voiceError, setVoiceError] = useState(null)
  const voiceSupported = typeof MediaRecorder !== 'undefined'

  // Claim stage
  const [stageConfirm, setStageConfirm] = useState(null) // key of stage being confirmed

  // Co-advisor management
  const [showAddAdvisor, setShowAddAdvisor] = useState(false)
  const [coSearch, setCoSearch] = useState('')

  // Reassignment history toggle
  const [showHistory, setShowHistory] = useState(false)

  // Watchers
  const [watchers, setWatchers] = useState([])  // [{task_id, advisor_id, advisors:{id,full_name,avatar_url}}]
  const [showAddWatcher, setShowAddWatcher] = useState(false)
  const [watcherSearch, setWatcherSearch] = useState('')

  // Checklist (sfp_subtasks — simple checkboxes)
  const [subtasks, setSubtasks] = useState([])
  const [newSubtask, setNewSubtask] = useState({ title: '', assigned_to: '', due_date: '' })
  const [addingSubtask, setAddingSubtask] = useState(false)

  // Full subtasks (sfp_tasks children with parent_task_id)
  const [fullSubtasks, setFullSubtasks] = useState([])
  const [parentTask, setParentTask] = useState(null)
  const [creatingSubtask, setCreatingSubtask] = useState(false)

  // ── Fetch ──────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!taskId) return
    setLoading(true)
    setTask(null)
    setComments([])
    setAttachments([])
    setReassignments([])
    setCoAdvisors([])
    setWatchers([])
    setSubtasks([])
    setFullSubtasks([])
    setParentTask(null)
    setCreatingSubtask(false)
    setActionPanel(null)

    const fetchAll = async () => {
      const [{ data: t }, { data: att }, { data: cmt }, { data: rea }, { data: coAdv }, { data: wtch }, { data: stsk }, { data: childTasks }] = await Promise.all([
        supabase.from('sfp_tasks')
          .select('*, advisors!advisor_id(full_name, avatar_url, email), completed_by_advisor:completed_by(full_name), reopened_by_advisor:reopened_by(full_name), parent:parent_task_id(id, title)')
          .eq('id', taskId).single(),
        supabase.from('sfp_task_attachments').select('*').eq('task_id', taskId).order('uploaded_at'),
        supabase.from('sfp_task_comments')
          .select('*, advisors(full_name, avatar_url)')
          .eq('task_id', taskId).order('created_at'),
        supabase.from('task_reassignments')
          .select('*, from_advisor:from_advisor_id(full_name), to_advisor:to_advisor_id(full_name), by_advisor:reassigned_by(full_name)')
          .eq('task_id', taskId).order('reassigned_at', { ascending: false }),
        supabase.from('sfp_task_advisors')
          .select('advisor_id, advisors!advisor_id(id, full_name, avatar_url)')
          .eq('task_id', taskId),
        supabase.from('sfp_task_watchers')
          .select('task_id, advisor_id, advisors!advisor_id(id, full_name, avatar_url)')
          .eq('task_id', taskId),
        supabase.from('sfp_subtasks')
          .select('*, advisors!assigned_to(id, full_name, avatar_url)')
          .eq('task_id', taskId)
          .order('created_at'),
        supabase.from('sfp_tasks')
          .select('id, title, status, priority, due_date, advisor_id, advisors!advisor_id(id, full_name, avatar_url)')
          .eq('parent_task_id', taskId)
          .is('deleted_at', null)
          .order('created_at'),
      ])
      setTask(t ?? null)
      setParentTask(t?.parent ?? null)
      setAttachments(att ?? [])
      setComments(cmt ?? [])
      setReassignments(rea ?? [])
      setCoAdvisors(coAdv ?? [])
      setWatchers(wtch ?? [])
      setSubtasks(stsk ?? [])
      setFullSubtasks(childTasks ?? [])

      // Fetch reactions for all comments in this task
      const commentIds = (cmt ?? []).map(c => c.id)
      const { data: rxns } = commentIds.length > 0
        ? await supabase.from('sfp_comment_reactions').select('comment_id, advisor_id, emoji').in('comment_id', commentIds)
        : { data: [] }
      const rxnMap = {}
      ;(rxns ?? []).forEach(r => {
        if (!rxnMap[r.comment_id]) rxnMap[r.comment_id] = []
        rxnMap[r.comment_id].push(r)
      })
      setReactionsMap(rxnMap)

      setLoading(false)
    }

    fetchAll()

    // Realtime: task updates
    const taskCh = supabase.channel(`panel-task-${taskId}`)
      .on('postgres_changes', { event: 'UPDATE', schema: 'public', table: 'sfp_tasks', filter: `id=eq.${taskId}` },
        (p) => setTask(prev => ({ ...prev, ...p.new })))
      .subscribe()

    // Realtime: new comments — re-fetch full row to include media columns (Fix 3)
    const cmtCh = supabase.channel(`panel-comments-${taskId}`)
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'sfp_task_comments', filter: `task_id=eq.${taskId}` },
        async (p) => {
          const { data: fullComment } = await supabase
            .from('sfp_task_comments')
            .select('*, advisors(full_name, avatar_url)')
            .eq('id', p.new.id)
            .single()
          if (fullComment) setComments(prev => [...prev, fullComment])
        })
      .subscribe()

    // Realtime: reactions
    const rxnCh = supabase.channel(`panel-reactions-${taskId}`)
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'sfp_comment_reactions' },
        p => setReactionsMap(prev => ({
          ...prev,
          [p.new.comment_id]: [...(prev[p.new.comment_id] ?? []), p.new],
        })))
      .on('postgres_changes', { event: 'DELETE', schema: 'public', table: 'sfp_comment_reactions' },
        p => setReactionsMap(prev => ({
          ...prev,
          [p.old.comment_id]: (prev[p.old.comment_id] ?? []).filter(
            r => !(r.advisor_id === p.old.advisor_id && r.emoji === p.old.emoji)
          ),
        })))
      .subscribe()

    return () => {
      supabase.removeChannel(taskCh)
      supabase.removeChannel(cmtCh)
      supabase.removeChannel(rxnCh)
    }
  }, [taskId])

  // Close menu on outside click
  useEffect(() => {
    if (!menuOpen) return
    const h = (e) => { if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false) }
    document.addEventListener('mousedown', h)
    return () => document.removeEventListener('mousedown', h)
  }, [menuOpen])

  // Scroll comments to bottom on new comment
  useEffect(() => {
    commentsEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [comments])

  // ── Helpers ────────────────────────────────────────────────────────────────
  const closeAction = () => {
    setActionPanel(null); setCompletionNotes(''); setNewFiles([])
    setReopenReason(''); setReassignToId(''); setReassignReason('')
    setDeleteReason(''); setExtendNewDate(''); setExtendReason('')
    setActionError(null)
  }

  // ── Delete task (soft delete) ───────────────────────────────────────────────
  const handleDelete = async () => {
    if (!deleteReason.trim()) { setActionError('A reason is required before deleting.'); return }
    if (!currentAdvisor) { setActionError('Could not identify current advisor.'); return }
    setSaving(true); setActionError(null)
    const { error } = await supabase.from('sfp_tasks').update({
      deleted_at:    new Date().toISOString(),
      deleted_by:    currentAdvisor.id,
      delete_reason: deleteReason.trim(),
    }).eq('id', taskId)
    if (error) { setActionError(error.message); setSaving(false); return }
    onTaskDeleted?.(taskId)
  }

  const notifyUpdate = (updated) => { onTaskUpdated?.(updated) }

  // ── Co-advisor management ──────────────────────────────────────────────────
  const handleAddCoAdvisor = async (advisor) => {
    const { error } = await supabase.from('sfp_task_advisors').insert({
      task_id: taskId, advisor_id: advisor.id, added_by: currentAdvisor?.id ?? null,
    })
    if (!error) {
      setCoAdvisors(prev => [...prev, {
        advisor_id: advisor.id,
        advisors: { id: advisor.id, full_name: advisor.full_name, avatar_url: advisor.avatar_url ?? null },
      }])

      // Notify the newly added co-advisor
      await supabase.from('sfp_notifications').insert({
        advisor_id: advisor.id,
        type: 'co_advisor_added',
        title: `You've been added to: ${task.claim_number || task.title}`,
        message: `${currentAdvisor.full_name} added you as a co-advisor`,
        task_id: taskId,
      })
    }
    setShowAddAdvisor(false)
    setCoSearch('')
  }

  const handleRemoveCoAdvisor = async (advisorId) => {
    const { error } = await supabase.from('sfp_task_advisors').delete()
      .eq('task_id', taskId).eq('advisor_id', advisorId)
    if (!error) setCoAdvisors(prev => prev.filter(a => a.advisor_id !== advisorId))
  }

  // Available advisors for co-advisor dropdown
  const coAdvisorOptions = advisors.filter(a =>
    a.id !== task?.advisor_id &&
    !coAdvisors.some(ca => ca.advisor_id === a.id) &&
    a.full_name.toLowerCase().includes(coSearch.toLowerCase())
  ).slice(0, 6)

  // ── @mention in comments ───────────────────────────────────────────────────
  const handleCommentChange = (e) => {
    const val = e.target.value
    setCommentBody(val)
    const cursor = e.target.selectionStart
    const textBefore = val.slice(0, cursor)
    const lastAt = textBefore.lastIndexOf('@')
    if (lastAt >= 0 && (lastAt === 0 || /\s/.test(textBefore[lastAt - 1]))) {
      const query = textBefore.slice(lastAt + 1)
      if (!query.includes(' ')) {
        setMentionSearch(query)
        setMentionOpen(true)
        return
      }
    }
    setMentionOpen(false)
  }

  const insertMention = (advisor) => {
    const el = commentInputRef.current
    const cursor = el?.selectionStart ?? commentBody.length
    const textBefore = commentBody.slice(0, cursor)
    const lastAt = textBefore.lastIndexOf('@')
    const newBody = commentBody.slice(0, lastAt) + `@${advisor.full_name} ` + commentBody.slice(cursor)
    setCommentBody(newBody)
    setMentionOpen(false)
    setMentionSearch('')
    setTimeout(() => el?.focus(), 0)
  }

  const filteredMentions = advisors.filter(a =>
    !mentionSearch || a.full_name.toLowerCase().includes(mentionSearch.toLowerCase())
  ).slice(0, 5)

  // ── Emoji picker ───────────────────────────────────────────────────────────
  const handleEmojiSelect = (emoji) => {
    const el = commentInputRef.current
    const cursor = el ? el.selectionStart : commentBody.length
    const newBody = commentBody.slice(0, cursor) + emoji + commentBody.slice(cursor)
    setCommentBody(newBody)
    setEmojiOpen(false)
    setTimeout(() => {
      el?.focus()
      el?.setSelectionRange(cursor + emoji.length, cursor + emoji.length)
    }, 0)
  }

  // ── File upload in chat ────────────────────────────────────────────────────
  const handleChatFileSelect = async (e) => {
    const file = e.target.files?.[0]
    if (!file || !currentAdvisor) return
    e.target.value = ''
    setUploadingFile(true)
    try {
      const filename = `${taskId}/${Date.now()}_${file.name}`
      const { error: upErr } = await supabase.storage.from('task_attachments').upload(filename, file, { upsert: false, contentType: file.type })
      if (upErr) throw new Error(upErr.message)
      const { data: urlData } = supabase.storage.from('task_attachments').getPublicUrl(filename)
      await supabase.from('sfp_task_comments').insert({
        task_id:      taskId,
        advisor_id:   currentAdvisor.id,
        body:         file.name,
        comment_type: 'file',
        media_url:    urlData.publicUrl,
        media_name:   file.name,
        media_size:   file.size,
        media_mime:   file.type,
      })
    } catch (err) {
      console.error('Chat file upload failed:', err)
    }
    setUploadingFile(false)
  }

  // ── Voice notes ────────────────────────────────────────────────────────────
  const handleVoiceToggle = async () => {
    if (!voiceSupported) return

    if (recording) {
      // Stop recording
      mediaRecorder?.stop()
      setRecording(false)
    } else {
      // Start recording
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
        audioChunksRef.current = []
        // Detect best supported MIME type (Safari needs mp4, Chrome/Firefox prefer webm)
        const mimeTypes = ['audio/webm;codecs=opus', 'audio/webm', 'audio/ogg;codecs=opus', 'audio/mp4']
        const bestMime = mimeTypes.find(t => MediaRecorder.isTypeSupported(t)) || ''
        const mr = new MediaRecorder(stream, bestMime ? { mimeType: bestMime } : {})
        mr.ondataavailable = (e) => { audioChunksRef.current.push(e.data) }
        mr.onstop = async () => {
          stream.getTracks().forEach(t => t.stop())
          if (!currentAdvisor || audioChunksRef.current.length === 0) return
          setUploadingVoice(true)
          try {
            const actualMime = mr.mimeType || bestMime || 'audio/webm'
            const ext = actualMime.includes('mp4') ? 'm4a' : actualMime.includes('ogg') ? 'ogg' : 'webm'
            const blob = new Blob(audioChunksRef.current, { type: actualMime })
            const filename = `${taskId}/${Date.now()}.${ext}`
            const { error: upErr } = await supabase.storage.from('task_voices').upload(filename, blob, { upsert: true, contentType: actualMime })
            if (upErr) throw new Error(upErr.message)
            const { data: urlData } = supabase.storage.from('task_voices').getPublicUrl(filename)
            await supabase.from('sfp_task_comments').insert({
              task_id:      taskId,
              advisor_id:   currentAdvisor.id,
              body:         'Voice note',
              comment_type: 'voice',
              media_url:    urlData.publicUrl,
              media_mime:   actualMime,
            })
          } catch (err) {
            console.error('Voice upload failed:', err)
          }
          setUploadingVoice(false)
        }
        mr.start()
        setMediaRecorder(mr)
        setRecording(true)
      } catch (err) {
        console.error('Microphone access denied:', err)
        if (err.name === 'NotFoundError') {
          setVoiceError('No microphone found on this device')
        } else if (err.name === 'NotAllowedError') {
          setVoiceError('Microphone access denied — check browser permissions')
        } else {
          setVoiceError('Could not start recording')
        }
        setTimeout(() => setVoiceError(null), 4000)
      }
    }
  }

  // ── Claim stage change ─────────────────────────────────────────────────────
  const handleStageChange = async (stageKey) => {
    if (!currentAdvisor) return
    const stageLabel = CLAIM_STAGES.find(s => s.key === stageKey)?.label ?? stageKey
    const now = new Date().toISOString()
    const { error } = await supabase.from('sfp_tasks').update({
      claim_stage:      stageKey,
      stage_updated_at: now,
      stage_updated_by: currentAdvisor.id,
    }).eq('id', taskId)
    if (error) { console.error('Stage update failed:', error); return }
    setTask(prev => ({ ...prev, claim_stage: stageKey, stage_updated_at: now }))
    setStageConfirm(null)

    // Notify co-advisors + lead (excluding self)
    const recipients = [task.advisor_id, ...coAdvisors.map(ca => ca.advisor_id)]
      .filter((id, i, arr) => id && id !== currentAdvisor.id && arr.indexOf(id) === i)
    if (recipients.length > 0) {
      await supabase.from('sfp_notifications').insert(
        recipients.map(advisor_id => ({
          advisor_id,
          type:    'claim_stage_changed',
          title:   `Claim stage updated: ${task.claim_number || task.title}`,
          message: `${currentAdvisor.full_name} moved the claim to "${stageLabel}"`,
          task_id: taskId,
        }))
      )
    }

    // Fire reverse sync to Dispatch (non-blocking — CRM works even if Dispatch is offline)
    supabase.functions.invoke('stage-to-dispatch', {
      body: { task_id: taskId, new_stage: stageKey },
    }).catch(err => console.warn('[stage-to-dispatch] Dispatch stage sync failed:', err))
  }

  // ── Priority change ────────────────────────────────────────────────────────
  const handlePriorityChange = async (newPriority) => {
    if (!newPriority || newPriority === task.priority) return
    const { error } = await supabase.from('sfp_tasks')
      .update({ priority: newPriority, updated_at: new Date().toISOString() })
      .eq('id', taskId)
    if (!error) {
      setTask(prev => ({ ...prev, priority: newPriority }))
      notifyUpdate({ ...task, priority: newPriority })
    }
  }

  // ── Emoji reactions ────────────────────────────────────────────────────────
  const handleToggleReaction = async (commentId, emoji) => {
    if (!currentAdvisor) return
    const existing = (reactionsMap[commentId] ?? []).find(
      r => r.advisor_id === currentAdvisor.id && r.emoji === emoji
    )
    if (existing) {
      // Optimistic remove
      setReactionsMap(prev => ({
        ...prev,
        [commentId]: (prev[commentId] ?? []).filter(
          r => !(r.advisor_id === currentAdvisor.id && r.emoji === emoji)
        ),
      }))
      await supabase.from('sfp_comment_reactions').delete()
        .eq('comment_id', commentId).eq('advisor_id', currentAdvisor.id).eq('emoji', emoji)
    } else {
      // Optimistic add
      const newReaction = { comment_id: commentId, advisor_id: currentAdvisor.id, emoji }
      setReactionsMap(prev => ({
        ...prev,
        [commentId]: [...(prev[commentId] ?? []), newReaction],
      }))
      await supabase.from('sfp_comment_reactions').insert(newReaction)
    }
  }

  // ── Start task ─────────────────────────────────────────────────────────────
  const handleStart = async () => {
    setSaving(true)
    const now = new Date().toISOString()
    const { error } = await supabase.from('sfp_tasks').update({ status: 'in_progress', updated_at: now }).eq('id', taskId)
    if (!error) { setTask(p => ({ ...p, status: 'in_progress' })); notifyUpdate({ ...task, status: 'in_progress' }) }
    setSaving(false)
    setMenuOpen(false)
  }

  // ── Complete ───────────────────────────────────────────────────────────────
  const handleComplete = async () => {
    if (!currentAdvisor) return
    setSaving(true); setActionError(null)
    const now = new Date().toISOString()
    try {
      const { error } = await supabase.from('sfp_tasks').update({
        status: 'completed', completed_at: now,
        completed_by: currentAdvisor.id,
        completion_notes: completionNotes || null, updated_at: now,
      }).eq('id', taskId)
      if (error) throw error

      for (const file of newFiles) {
        const filename = `${taskId}/${Date.now()}_${file.name}`
        const { error: upErr } = await supabase.storage.from('task_attachments').upload(filename, file, { upsert: true })
        if (upErr) throw new Error(upErr.message)
        const { data: url } = supabase.storage.from('task_attachments').getPublicUrl(filename)
        await supabase.from('sfp_task_attachments').insert({ task_id: taskId, file_name: file.name, file_url: url.publicUrl, file_size: file.size, mime_type: file.type, source: 'adviser' })
        setAttachments(p => [...p, { id: crypto.randomUUID(), task_id: taskId, file_name: file.name, file_url: url.publicUrl, file_size: file.size, mime_type: file.type, source: 'adviser', uploaded_at: now }])
      }

      const updated = { ...task, status: 'completed', completed_at: now, completed_by: currentAdvisor.id, completion_notes: completionNotes || null, completed_by_advisor: { full_name: currentAdvisor.full_name } }
      setTask(updated); notifyUpdate(updated); closeAction()
    } catch (e) { setActionError(e.message) }
    setSaving(false)
  }

  // ── Reopen ─────────────────────────────────────────────────────────────────
  const handleReopen = async () => {
    if (!currentAdvisor || !reopenReason.trim()) { setActionError('Reason required.'); return }
    setSaving(true); setActionError(null)
    const now = new Date().toISOString()
    const { error } = await supabase.from('sfp_tasks').update({
      status: 'open', reopened_reason: reopenReason.trim(), reopened_at: now,
      reopened_by: currentAdvisor.id, completed_at: null, completed_by: null, completion_notes: null, updated_at: now,
    }).eq('id', taskId)
    if (error) { setActionError(error.message) } else {
      const updated = { ...task, status: 'open', reopened_reason: reopenReason.trim(), reopened_at: now, completed_at: null, completion_notes: null }
      setTask(updated); notifyUpdate(updated); closeAction()
    }
    setSaving(false)
  }

  // ── Reassign ───────────────────────────────────────────────────────────────
  const handleReassign = async () => {
    if (!currentAdvisor || !reassignToId) { setActionError('Select an adviser.'); return }
    setSaving(true); setActionError(null)
    try {
      const now = new Date().toISOString()
      const { error: auditErr } = await supabase.from('task_reassignments').insert({
        task_id: taskId, from_advisor_id: task.advisor_id, to_advisor_id: reassignToId,
        reason: reassignReason.trim() || null, reassigned_by: currentAdvisor.id,
      })
      if (auditErr) throw auditErr
      const { error: updateErr } = await supabase.from('sfp_tasks').update({ advisor_id: reassignToId, updated_at: now }).eq('id', taskId)
      if (updateErr) throw updateErr
      await supabase.from('sfp_notifications').insert({
        advisor_id: reassignToId, type: 'task_reassigned',
        title: `New task assigned: ${task.claim_number || task.title}`,
        message: `${currentAdvisor.full_name} assigned you a task`,
        task_id: taskId, claim_id: task.claim_id,
      })
      notifyUpdate({ ...task, advisor_id: reassignToId })
      onClose()
    } catch (e) { setActionError(e.message); setSaving(false) }
  }

  // ── Extend due date ────────────────────────────────────────────────────────
  const handleExtendDueDate = async () => {
    if (!extendNewDate) { setActionError('Select a new due date.'); return }
    if (!extendReason.trim()) { setActionError('A reason is required.'); return }
    if (!currentAdvisor) return
    setSaving(true); setActionError(null)
    const now = new Date().toISOString()
    const oldDate = task.due_date
    const newDate = new Date(extendNewDate).toISOString()
    try {
      const { error: updateErr } = await supabase
        .from('sfp_tasks')
        .update({ due_date: newDate, updated_at: now })
        .eq('id', taskId)
      if (updateErr) throw updateErr

      // Write paper trail record as a structured comment
      await supabase.from('sfp_task_comments').insert({
        task_id:      taskId,
        advisor_id:   currentAdvisor.id,
        body:         JSON.stringify({ old_date: oldDate, new_date: newDate, reason: extendReason.trim() }),
        comment_type: 'due_extension',
      })

      const updated = { ...task, due_date: newDate }
      setTask(updated); notifyUpdate(updated); closeAction()
    } catch (e) { setActionError(e.message) }
    setSaving(false)
  }

  // ── Watcher management ────────────────────────────────────────────────────────
  const addWatcher = async (advisorId) => {
    try {
      const { error } = await supabase.from('sfp_task_watchers').insert({
        task_id: taskId,
        advisor_id: advisorId,
        added_by: currentAdvisor?.id,
      })
      if (error) throw error

      // Notify new watcher
      await supabase.from('sfp_notifications').insert({
        advisor_id: advisorId,
        type: 'watcher_added',
        title: 'Added as task watcher',
        task_id: taskId,
      })

      // Refetch watchers
      const { data } = await supabase.from('sfp_task_watchers')
        .select('task_id, advisor_id, advisors!advisor_id(id, full_name, avatar_url)')
        .eq('task_id', taskId)
      setWatchers(data ?? [])
      setShowAddWatcher(false)
      setWatcherSearch('')
    } catch (err) {
      console.error('Failed to add watcher:', err)
    }
  }

  const removeWatcher = async (advisorId) => {
    try {
      const { error } = await supabase.from('sfp_task_watchers')
        .delete()
        .eq('task_id', taskId)
        .eq('advisor_id', advisorId)
      if (error) throw error

      setWatchers(prev => prev.filter(w => w.advisor_id !== advisorId))
    } catch (err) {
      console.error('Failed to remove watcher:', err)
    }
  }

  // ── Subtask management ──────────────────────────────────────────────────────
  const createSubtask = async () => {
    if (!newSubtask.title.trim()) return
    try {
      const { error } = await supabase.from('sfp_subtasks').insert({
        task_id: taskId,
        title: newSubtask.title.trim(),
        assigned_to: newSubtask.assigned_to || null,
        due_date: newSubtask.due_date || null,
        created_by: currentAdvisor?.id,
      })
      if (error) throw error

      // Notify assignee if set
      if (newSubtask.assigned_to) {
        await supabase.from('sfp_notifications').insert({
          advisor_id: newSubtask.assigned_to,
          type: 'subtask_assigned',
          title: `Assigned to subtask: ${newSubtask.title}`,
          task_id: taskId,
        })
      }

      // Refetch subtasks
      const { data } = await supabase.from('sfp_subtasks')
        .select('*, advisors!assigned_to(id, full_name, avatar_url)')
        .eq('task_id', taskId)
        .order('created_at')
      setSubtasks(data ?? [])
      setNewSubtask({ title: '', assigned_to: '', due_date: '' })
      setAddingSubtask(false)
    } catch (err) {
      console.error('Failed to create subtask:', err)
    }
  }

  const toggleSubtask = async (subtaskId, completed) => {
    try {
      const { error } = await supabase.from('sfp_subtasks')
        .update({
          completed: !completed,
          completed_at: !completed ? new Date().toISOString() : null,
        })
        .eq('id', subtaskId)
      if (error) throw error

      setSubtasks(prev =>
        prev.map(st =>
          st.id === subtaskId
            ? { ...st, completed: !completed, completed_at: !completed ? new Date().toISOString() : null }
            : st
        )
      )
    } catch (err) {
      console.error('Failed to toggle subtask:', err)
    }
  }

  const deleteSubtask = async (subtaskId) => {
    try {
      const { error } = await supabase.from('sfp_subtasks').delete().eq('id', subtaskId)
      if (error) throw error
      setSubtasks(prev => prev.filter(st => st.id !== subtaskId))
    } catch (err) {
      console.error('Failed to delete subtask:', err)
    }
  }

  // ── Add comment ────────────────────────────────────────────────────────────
  const handleAddComment = async (e) => {
    e.preventDefault()
    const body = commentBody.trim()
    if (!body || !currentAdvisor) return
    setSendingComment(true)
    const { error } = await supabase.from('sfp_task_comments').insert({
      task_id: taskId, advisor_id: currentAdvisor.id, body, comment_type: 'text',
    })
    if (!error) {
      setCommentBody('')

      // Parse @mentions and notify
      const mentionPattern = /@([A-Za-z\s]+?)(?=\s@|\s*$|[^A-Za-z\s])/g
      const mentioned = []
      let m
      const bodyForParsing = body + ' ' // ensure trailing space for regex
      while ((m = mentionPattern.exec(bodyForParsing)) !== null) {
        const name = m[1].trim()
        const found = advisors.find(a => a.full_name.toLowerCase() === name.toLowerCase())
        if (found && found.id !== currentAdvisor.id) mentioned.push(found.id)
      }
      if (mentioned.length > 0) {
        await supabase.from('sfp_notifications').insert(
          [...new Set(mentioned)].map(advisor_id => ({
            advisor_id,
            type:    'mention',
            title:   `${currentAdvisor.full_name} mentioned you`,
            message: `"${body.slice(0, 80)}${body.length > 80 ? '…' : ''}"`,
            task_id: taskId,
          }))
        )
      }

      // Notify task participants (lead + co-advisors + watchers, not commenter)
      const participants = [
        task.advisor_id,
        ...coAdvisors.map(ca => ca.advisor_id),
        ...watchers.map(w => w.advisor_id),
        task.created_by
      ].filter((id, i, arr) => id && id !== currentAdvisor.id && arr.indexOf(id) === i)
      if (participants.length > 0) {
        await supabase.from('sfp_notifications').insert(
          participants.map(advisor_id => ({
            advisor_id,
            type:    'new_comment',
            title:   `New comment on: ${task.claim_number || task.title}`,
            message: `${currentAdvisor.full_name}: "${body.slice(0, 60)}${body.length > 60 ? '…' : ''}"`,
            task_id: taskId,
          }))
        )
      }
    }
    setSendingComment(false)
  }

  // ── Styles ─────────────────────────────────────────────────────────────────
  const bg = isDarkMode ? 'bg-slate-900 text-slate-100' : 'bg-white text-gray-900'
  const border = isDarkMode ? 'border-slate-700' : 'border-gray-200'
  const muted = isDarkMode ? 'text-slate-400' : 'text-gray-500'
  const cardBg = isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-gray-50 border-gray-200'
  const inputCls = isDarkMode
    ? 'bg-slate-700 border-slate-600 text-slate-100 placeholder-slate-400 focus:border-sfp-orange'
    : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400 focus:border-sfp-orange'
  const cancelBtn = isDarkMode ? 'bg-slate-600 text-slate-200 hover:bg-slate-500' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
  const panelBg = isDarkMode ? 'bg-slate-800 border-slate-600' : 'bg-blue-50 border-blue-200'

  if (loading) return (
    <div className={`flex items-center justify-center h-full ${muted}`}>Loading…</div>
  )
  if (!task) return (
    <div className={`flex items-center justify-center h-full ${muted}`}>Task not found.</div>
  )

  const isOpen = task.status === 'open'
  const isInProgress = task.status === 'in_progress'
  const isCompleted = task.status === 'completed'
  const otherAdvisors = advisors.filter(a => a.id !== task.advisor_id && a.active !== false)

  // Build activity feed
  const activity = []
  activity.push({ type: 'created', ts: task.created_at, label: 'Task created' })
  if (task.completed_at) activity.push({ type: 'completed', ts: task.completed_at, label: `Marked complete${task.completed_by_advisor?.full_name ? ` by ${task.completed_by_advisor.full_name}` : ''}` })
  if (task.reopened_at) activity.push({ type: 'reopened', ts: task.reopened_at, label: `Reopened${task.reopened_reason ? ` — "${task.reopened_reason}"` : ''}` })
  reassignments.forEach(r => activity.push({
    type: 'reassigned', ts: r.reassigned_at,
    label: `Reassigned from ${r.from_advisor?.full_name ?? '?'} to ${r.to_advisor?.full_name ?? '?'}${r.reason ? ` — "${r.reason}"` : ''}`,
  }))
  comments.filter(c => c.comment_type === 'due_extension').forEach(c => {
    try {
      const data = JSON.parse(c.body)
      const by = c.advisors?.full_name ? ` by ${c.advisors.full_name}` : ''
      const newD = data.new_date ? new Date(data.new_date).toLocaleDateString('en-ZA') : '?'
      activity.push({
        type: 'extended', ts: c.created_at,
        label: `Due date extended to ${newD}${by}${data.reason ? ` — "${data.reason}"` : ''}`,
      })
    } catch {
      return
    }
  })
  activity.sort((a, b) => new Date(a.ts) - new Date(b.ts))

  return (
    <div className={`flex flex-col h-full overflow-hidden ${bg}`}>
      {/* ── Header ── */}
      <div className={`flex items-start gap-2 px-4 pt-4 pb-3 border-b flex-shrink-0 ${border}`}>
        <div className="flex-1 min-w-0">
          {parentTask && (
            <button
              onClick={() => onOpenTask?.(parentTask.id)}
              className="flex items-center gap-1 text-xs text-sfp-orange hover:underline mb-1"
            >
              <ChevronLeft className="w-3 h-3" />
              {parentTask.title}
            </button>
          )}
          <div className="flex items-center gap-2 flex-wrap">
            <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium flex-shrink-0 ${STATUS_COLORS[task.status] ?? STATUS_COLORS.open}`}>
              {task.status.replace('_', ' ')}
            </span>
            {task.section && (
              <span className={`px-2 py-0.5 rounded text-xs font-medium ${SECTION_COLORS[task.section] ?? 'bg-gray-100 text-gray-600'}`}>
                {task.section}
              </span>
            )}
          </div>
          <h2 className="text-base font-bold mt-1 leading-tight">
            {task.claim_number || task.title}
          </h2>
          {task.client_name && task.client_name !== '—' && (
            <p className={`text-xs mt-0.5 ${muted}`}>{task.client_name}</p>
          )}
        </div>

        {/* Action menu */}
        <div className="flex items-center gap-1 flex-shrink-0" ref={menuRef}>
          <div className="relative">
            <button
              onClick={() => setMenuOpen(v => !v)}
              className={`p-1.5 rounded hover:bg-gray-100 dark:hover:bg-slate-700 transition ${muted}`}
              title="Actions"
            >
              <MoreHorizontal className="w-4 h-4" />
            </button>
            {menuOpen && (
              <div className={`absolute right-0 top-8 w-44 rounded-lg shadow-xl border z-50 py-1 ${isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-gray-200'}`}>
                {isOpen && (
                  <button onClick={handleStart} disabled={saving}
                    className={`w-full text-left px-3 py-2 text-sm flex items-center gap-2 hover:bg-gray-50 dark:hover:bg-slate-700 ${isDarkMode ? 'text-slate-200' : 'text-gray-700'}`}>
                    <CheckCircle className="w-3.5 h-3.5 text-blue-500" /> Start Task
                  </button>
                )}
                {(isOpen || isInProgress) && (
                  <button onClick={() => { setActionPanel('complete'); setMenuOpen(false) }}
                    className={`w-full text-left px-3 py-2 text-sm flex items-center gap-2 hover:bg-gray-50 dark:hover:bg-slate-700 ${isDarkMode ? 'text-slate-200' : 'text-gray-700'}`}>
                    <CheckCircle className="w-3.5 h-3.5 text-green-500" /> Mark Complete
                  </button>
                )}
                {isCompleted && (
                  <button onClick={() => { setActionPanel('reopen'); setMenuOpen(false) }}
                    className={`w-full text-left px-3 py-2 text-sm flex items-center gap-2 hover:bg-gray-50 dark:hover:bg-slate-700 ${isDarkMode ? 'text-slate-200' : 'text-gray-700'}`}>
                    <RotateCcw className="w-3.5 h-3.5 text-yellow-500" /> Reopen Task
                  </button>
                )}
                <button onClick={() => { setActionPanel('reassign'); setMenuOpen(false) }}
                  className={`w-full text-left px-3 py-2 text-sm flex items-center gap-2 hover:bg-gray-50 dark:hover:bg-slate-700 ${isDarkMode ? 'text-slate-200' : 'text-gray-700'}`}>
                  <UserCheck className="w-3.5 h-3.5 text-blue-500" /> Reassign Lead
                </button>
                {(isOpen || isInProgress) && (
                  <button onClick={() => { setActionPanel('extend_due'); setMenuOpen(false) }}
                    className={`w-full text-left px-3 py-2 text-sm flex items-center gap-2 hover:bg-gray-50 dark:hover:bg-slate-700 ${isDarkMode ? 'text-slate-200' : 'text-gray-700'}`}>
                    <CalendarClock className="w-3.5 h-3.5 text-orange-500" /> Extend Due Date
                  </button>
                )}
                <div className={`my-1 border-t ${isDarkMode ? 'border-slate-700' : 'border-gray-100'}`} />
                <button onClick={() => { setActionPanel('delete'); setMenuOpen(false) }}
                  className="w-full text-left px-3 py-2 text-sm flex items-center gap-2 hover:bg-red-50 dark:hover:bg-red-900/20 text-red-600">
                  <Trash2 className="w-3.5 h-3.5" /> Delete Task
                </button>
              </div>
            )}
          </div>
          <button onClick={onClose} className={`p-1.5 rounded hover:bg-gray-100 dark:hover:bg-slate-700 transition ${muted}`}>
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* ── Scrollable body ── */}
      <div className="flex-1 overflow-y-auto px-4 py-3 space-y-4">

        {/* Action Panels */}
        {actionPanel === 'complete' && (
          <div className={`p-3 rounded-lg border ${panelBg}`}>
            <p className="text-xs font-semibold mb-2 text-green-700 flex items-center gap-1">
              <CheckCircle className="w-3.5 h-3.5" /> Mark Complete
            </p>
            <textarea name="completion-notes" value={completionNotes} onChange={e => setCompletionNotes(e.target.value)}
              rows={2} placeholder="Completion notes (optional)"
              className={`w-full text-xs rounded border px-2 py-1.5 resize-none focus:outline-none mb-2 ${inputCls}`} />
            <input type="file" name="completion-files" multiple accept=".pdf,.png,.jpg,.jpeg,.docx"
              onChange={e => { setNewFiles(p => [...p, ...Array.from(e.target.files)]); e.target.value = '' }}
              className={`w-full text-xs mb-1 rounded border px-1.5 py-1 ${inputCls}`} />
            {newFiles.map((f, i) => (
              <div key={i} className="flex items-center justify-between text-xs bg-green-50 border border-green-200 rounded px-2 py-0.5 mb-1">
                <span className="flex items-center gap-1"><Paperclip className="w-3 h-3 text-green-600" />{f.name}</span>
                <button onClick={() => setNewFiles(p => p.filter((_, j) => j !== i))} className="text-red-500 ml-1">✕</button>
              </div>
            ))}
            {actionError && <p className="text-xs text-red-500 mb-1">{actionError}</p>}
            <div className="flex gap-2">
              <button onClick={handleComplete} disabled={saving}
                className="px-3 py-1 bg-green-600 text-white rounded text-xs hover:bg-green-700 disabled:opacity-50">
                {saving ? 'Saving…' : 'Confirm'}
              </button>
              <button onClick={closeAction} className={`px-3 py-1 rounded text-xs ${cancelBtn}`}>Cancel</button>
            </div>
          </div>
        )}

        {actionPanel === 'reopen' && (
          <div className={`p-3 rounded-lg border ${panelBg}`}>
            <p className="text-xs font-semibold mb-2 text-yellow-700 flex items-center gap-1">
              <RotateCcw className="w-3.5 h-3.5" /> Reopen Task
            </p>
            <textarea name="reopen-reason" value={reopenReason} onChange={e => setReopenReason(e.target.value)}
              rows={2} placeholder="Reason for reopening *"
              className={`w-full text-xs rounded border px-2 py-1.5 resize-none focus:outline-none mb-2 ${inputCls}`} />
            {actionError && <p className="text-xs text-red-500 mb-1">{actionError}</p>}
            <div className="flex gap-2">
              <button onClick={handleReopen} disabled={saving}
                className="px-3 py-1 bg-yellow-500 text-white rounded text-xs hover:bg-yellow-600 disabled:opacity-50">
                {saving ? 'Saving…' : 'Confirm'}
              </button>
              <button onClick={closeAction} className={`px-3 py-1 rounded text-xs ${cancelBtn}`}>Cancel</button>
            </div>
          </div>
        )}

        {actionPanel === 'delete' && (
          <div className={`p-3 rounded-lg border border-red-300 ${isDarkMode ? 'bg-red-900/20' : 'bg-red-50'}`}>
            <p className="text-xs font-semibold mb-1 text-red-700 flex items-center gap-1">
              <Trash2 className="w-3.5 h-3.5" /> Delete Task
            </p>
            <p className={`text-xs mb-2 ${isDarkMode ? 'text-red-300' : 'text-red-600'}`}>
              This moves the task to the Bin. You can restore it from the Bin view. Please provide a reason (e.g. "Duplicate entry").
            </p>
            <textarea
              name="delete-reason"
              value={deleteReason}
              onChange={e => setDeleteReason(e.target.value)}
              rows={2}
              placeholder="Reason for deletion *"
              className={`w-full text-xs rounded border px-2 py-1.5 resize-none focus:outline-none mb-2 border-red-300 ${inputCls}`}
            />
            {actionError && <p className="text-xs text-red-500 mb-1">{actionError}</p>}
            <div className="flex gap-2">
              <button
                onClick={handleDelete}
                disabled={saving || !deleteReason.trim()}
                className="px-3 py-1 bg-red-600 text-white rounded text-xs hover:bg-red-700 disabled:opacity-50"
              >
                {saving ? 'Deleting…' : 'Confirm Delete'}
              </button>
              <button onClick={closeAction} className={`px-3 py-1 rounded text-xs ${cancelBtn}`}>Cancel</button>
            </div>
          </div>
        )}

        {actionPanel === 'reassign' && (
          <div className={`p-3 rounded-lg border ${panelBg}`}>
            <p className="text-xs font-semibold mb-2 text-blue-700 flex items-center gap-1">
              <UserCheck className="w-3.5 h-3.5" /> Reassign Lead Advisor
            </p>
            <select name="reassign-to" value={reassignToId} onChange={e => setReassignToId(e.target.value)}
              className={`w-full text-xs rounded border px-2 py-1.5 focus:outline-none mb-2 ${inputCls}`}>
              <option value="">— Select adviser —</option>
              {otherAdvisors.map(a => <option key={a.id} value={a.id}>{a.full_name}</option>)}
            </select>
            <textarea name="reassign-reason" value={reassignReason} onChange={e => setReassignReason(e.target.value)}
              rows={2} placeholder="Reason (optional)"
              className={`w-full text-xs rounded border px-2 py-1.5 resize-none focus:outline-none mb-2 ${inputCls}`} />
            {actionError && <p className="text-xs text-red-500 mb-1">{actionError}</p>}
            <div className="flex gap-2">
              <button onClick={handleReassign} disabled={saving || !reassignToId}
                className="px-3 py-1 bg-blue-600 text-white rounded text-xs hover:bg-blue-700 disabled:opacity-50">
                {saving ? 'Reassigning…' : 'Confirm'}
              </button>
              <button onClick={closeAction} className={`px-3 py-1 rounded text-xs ${cancelBtn}`}>Cancel</button>
            </div>
          </div>
        )}

        {actionPanel === 'extend_due' && (
          <div className={`p-3 rounded-lg border ${panelBg}`}>
            <p className="text-xs font-semibold mb-2 text-orange-600 flex items-center gap-1.5">
              <CalendarClock className="w-3.5 h-3.5" /> Extend Due Date
            </p>
            <p className={`text-xs mb-2 ${muted}`}>
              Current due date: <strong>{task.due_date ? new Date(task.due_date).toLocaleDateString('en-ZA') : 'Not set'}</strong>
            </p>
            <label className={`text-xs block mb-1 ${muted}`}>New due date</label>
            <input
              type="date"
              value={extendNewDate}
              onChange={e => setExtendNewDate(e.target.value)}
              min={new Date().toISOString().split('T')[0]}
              className={`w-full text-xs rounded border px-2 py-1.5 focus:outline-none mb-2 ${inputCls}`}
            />
            <label className={`text-xs block mb-1 ${muted}`}>Reason for extension <span className="text-red-500">*</span></label>
            <textarea
              value={extendReason}
              onChange={e => setExtendReason(e.target.value)}
              rows={2}
              placeholder="e.g. Client requires more time to gather supporting documents"
              className={`w-full text-xs rounded border px-2 py-1.5 resize-none focus:outline-none mb-2 ${inputCls}`}
            />
            {actionError && <p className="text-xs text-red-500 mb-1">{actionError}</p>}
            <div className="flex gap-2">
              <button
                onClick={handleExtendDueDate}
                disabled={saving || !extendNewDate || !extendReason.trim()}
                className="px-3 py-1 bg-orange-500 text-white rounded text-xs hover:bg-orange-600 disabled:opacity-50">
                {saving ? 'Saving…' : 'Confirm Extension'}
              </button>
              <button onClick={closeAction} className={`px-3 py-1 rounded text-xs ${cancelBtn}`}>Cancel</button>
            </div>
          </div>
        )}

        {/* ── Assignees ── */}
        <div>
          <p className={`text-xs font-medium mb-2 flex items-center gap-1 ${muted}`}>
            <User className="w-3 h-3" /> Assignees
          </p>

          {/* Advisor chips */}
          <div className="flex flex-wrap gap-2 mb-2">
            {/* Lead advisor */}
            <div className={`flex items-center gap-1.5 px-2 py-1 rounded-full text-xs border ${
              isDarkMode ? 'bg-sfp-orange/10 border-sfp-orange/40 text-slate-100' : 'bg-orange-50 border-orange-200 text-gray-800'
            }`}>
              <AdvisorAvatar avatarUrl={task.advisors?.avatar_url ?? null} fullName={task.advisors?.full_name ?? ''} size="sm" />
              <span className="font-medium">{task.advisors?.full_name ?? '—'}</span>
              <span className={`text-xs ${muted}`}>(Lead)</span>
            </div>

            {/* Co-advisors */}
            {coAdvisors.map(ca => (
              <div key={ca.advisor_id} className={`flex items-center gap-1.5 px-2 py-1 rounded-full text-xs border ${
                isDarkMode ? 'bg-slate-700 border-slate-600 text-slate-200' : 'bg-blue-50 border-blue-200 text-blue-800'
              }`}>
                <AdvisorAvatar avatarUrl={ca.advisors?.avatar_url ?? null} fullName={ca.advisors?.full_name ?? ''} size="sm" />
                <span className="font-medium">{ca.advisors?.full_name ?? '—'}</span>
                <button
                  onClick={() => handleRemoveCoAdvisor(ca.advisor_id)}
                  className="ml-0.5 text-gray-400 hover:text-red-500 leading-none transition"
                  title="Remove"
                >
                  ×
                </button>
              </div>
            ))}

            {/* Add advisor button */}
            <button
              onClick={() => { setShowAddAdvisor(v => !v); setCoSearch('') }}
              className={`flex items-center gap-1 px-2 py-1 rounded-full border text-xs transition ${
                isDarkMode
                  ? 'border-slate-600 text-slate-400 hover:border-sfp-orange hover:text-sfp-orange'
                  : 'border-gray-300 text-gray-400 hover:border-sfp-orange hover:text-sfp-orange'
              }`}
            >
              <UserPlus className="w-3 h-3" /> Add
            </button>
          </div>

          {/* Co-advisor search dropdown */}
          {showAddAdvisor && (
            <div className="relative">
              <input
                autoFocus
                type="text"
                name="co-advisor-search"
                value={coSearch}
                onChange={e => setCoSearch(e.target.value)}
                onBlur={() => setTimeout(() => setShowAddAdvisor(false), 150)}
                placeholder="Type advisor name…"
                className={`w-full text-xs rounded-lg border px-2.5 py-1.5 focus:outline-none focus:ring-1 focus:ring-sfp-orange ${inputCls}`}
              />
              {coAdvisorOptions.length > 0 && (
                <div className={`absolute top-full left-0 right-0 z-50 rounded-lg border shadow-lg mt-1 max-h-36 overflow-y-auto ${
                  isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-gray-200'
                }`}>
                  {coAdvisorOptions.map(a => (
                    <button
                      key={a.id}
                      onMouseDown={() => handleAddCoAdvisor(a)}
                      className={`w-full text-left px-2.5 py-2 text-xs flex items-center gap-2 transition hover:bg-sfp-orange/10 ${
                        isDarkMode ? 'text-slate-200' : 'text-gray-700'
                      }`}
                    >
                      <AdvisorAvatar avatarUrl={a.avatar_url ?? null} fullName={a.full_name} size="sm" />
                      {a.full_name}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* ── Watchers ── */}
        <div>
          <p className={`text-xs font-medium mb-2 ${muted}`}>Watchers</p>
          <div className="flex items-center gap-2 flex-wrap">
            {watchers.map(w => (
              <div key={w.advisor_id} className={`flex items-center gap-1.5 px-2 py-1 rounded-full text-xs border ${
                isDarkMode ? 'bg-slate-700 border-slate-600 text-slate-200' : 'bg-purple-50 border-purple-200 text-purple-800'
              }`}>
                <span>👁</span>
                <AdvisorAvatar avatarUrl={w.advisors?.avatar_url ?? null} fullName={w.advisors?.full_name ?? ''} size="sm" />
                <span className="font-medium">{w.advisors?.full_name ?? '—'}</span>
                <button
                  onClick={() => removeWatcher(w.advisor_id)}
                  className="ml-0.5 text-gray-400 hover:text-red-500 leading-none transition"
                  title="Remove"
                >
                  ×
                </button>
              </div>
            ))}

            {/* Add watcher button */}
            <button
              onClick={() => { setShowAddWatcher(v => !v); setWatcherSearch('') }}
              className={`flex items-center gap-1 px-2 py-1 rounded-full border text-xs transition ${
                isDarkMode
                  ? 'border-slate-600 text-slate-400 hover:border-sfp-orange hover:text-sfp-orange'
                  : 'border-gray-300 text-gray-400 hover:border-sfp-orange hover:text-sfp-orange'
              }`}
            >
              <UserPlus className="w-3 h-3" /> Add Watcher
            </button>
          </div>

          {/* Watcher search dropdown */}
          {showAddWatcher && (
            <div className="relative mt-2">
              <input
                autoFocus
                type="text"
                name="watcher-search"
                value={watcherSearch}
                onChange={e => setWatcherSearch(e.target.value)}
                onBlur={() => setTimeout(() => setShowAddWatcher(false), 150)}
                placeholder="Type advisor name…"
                className={`w-full text-xs rounded-lg border px-2.5 py-1.5 focus:outline-none focus:ring-1 focus:ring-sfp-orange ${inputCls}`}
              />
              {advisors
                .filter(a =>
                  a.id !== task.advisor_id &&
                  !watchers.some(w => w.advisor_id === a.id) &&
                  !coAdvisors.some(c => c.advisor_id === a.id) &&
                  a.full_name.toLowerCase().includes(watcherSearch.toLowerCase())
                )
                .length > 0 && (
                <div className={`absolute top-full left-0 right-0 z-50 rounded-lg border shadow-lg mt-1 max-h-36 overflow-y-auto ${
                  isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-gray-200'
                }`}>
                  {advisors
                    .filter(a =>
                      a.id !== task.advisor_id &&
                      !watchers.some(w => w.advisor_id === a.id) &&
                      !coAdvisors.some(c => c.advisor_id === a.id) &&
                      a.full_name.toLowerCase().includes(watcherSearch.toLowerCase())
                    )
                    .map(a => (
                      <button
                        key={a.id}
                        onMouseDown={() => addWatcher(a.id)}
                        className={`w-full text-left px-2.5 py-2 text-xs flex items-center gap-2 transition hover:bg-sfp-orange/10 ${
                          isDarkMode ? 'text-slate-200' : 'text-gray-700'
                        }`}
                      >
                        <AdvisorAvatar avatarUrl={a.avatar_url ?? null} fullName={a.full_name} size="sm" />
                        {a.full_name}
                      </button>
                    ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* ── Claim Progress Bar ── */}
        {(task.task_type === 'claim' || task.claim_number) && (() => {
          const currentIdx = CLAIM_STAGES.findIndex(s => s.key === (task.claim_stage ?? 'received'))
          const isStale = task.claim_stage !== 'settled' && task.claim_stage !== 'declined' &&
            task.stage_updated_at && ((Date.now() - new Date(task.stage_updated_at).getTime()) > 3 * 24 * 60 * 60 * 1000)
          return (
            <div>
              <p className={`text-xs font-medium mb-2 ${muted}`}>Claim Stage</p>

              {isStale && (
                <div className="mb-2 flex items-center gap-1.5 text-xs text-yellow-700 bg-yellow-50 border border-yellow-200 rounded px-2 py-1.5">
                  <span>⚠️</span> Stage not updated in 3+ days
                </div>
              )}

              <div className="flex items-center gap-1 overflow-x-auto pb-1">
                {CLAIM_STAGES.map((stage, idx) => {
                  const isDone = idx < currentIdx
                  const isCurrent = idx === currentIdx
                  const isFuture = idx > currentIdx
                  const isConfirming = stageConfirm === stage.key
                  return (
                    <div key={stage.key} className="flex items-center gap-1 flex-shrink-0">
                      {idx > 0 && (
                        <div className={`w-3 h-0.5 flex-shrink-0 ${isDone || isCurrent ? 'bg-sfp-orange' : isDarkMode ? 'bg-slate-600' : 'bg-gray-200'}`} />
                      )}
                      <div className="flex flex-col items-center gap-0.5">
                        {isConfirming ? (
                          <div className="flex flex-col items-center gap-1">
                            <span className={`text-xs text-center ${isDarkMode ? 'text-slate-300' : 'text-gray-700'}`}>Move to {stage.label}?</span>
                            <div className="flex gap-1">
                              <button onClick={() => handleStageChange(stage.key)}
                                className="px-1.5 py-0.5 bg-sfp-orange text-white rounded text-xs hover:opacity-90">Yes</button>
                              <button onClick={() => setStageConfirm(null)}
                                className={`px-1.5 py-0.5 rounded text-xs ${cancelBtn}`}>No</button>
                            </div>
                          </div>
                        ) : (
                          <button
                            onClick={() => isFuture ? setStageConfirm(stage.key) : undefined}
                            disabled={!isFuture}
                            title={isFuture ? `Move to ${stage.label}` : undefined}
                            className={`w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold transition ${
                              isCurrent ? `${stage.color} text-white shadow-sm` :
                              isDone    ? 'bg-sfp-orange text-white' :
                              isFuture  ? (isDarkMode ? 'bg-slate-600 text-slate-400 hover:bg-slate-500 cursor-pointer' : 'bg-gray-200 text-gray-400 hover:bg-gray-300 cursor-pointer') :
                              'bg-gray-200 text-gray-400'
                            }`}
                          >
                            {isDone ? '✓' : idx + 1}
                          </button>
                        )}
                        {!isConfirming && (
                          <span className={`text-[9px] text-center leading-tight max-w-[48px] ${
                            isCurrent ? 'font-bold text-sfp-orange' :
                            isDone    ? (isDarkMode ? 'text-slate-300' : 'text-gray-600') :
                            muted
                          }`}>
                            {stage.label}
                          </span>
                        )}
                      </div>
                    </div>
                  )
                })}
              </div>
            </div>
          )
        })()}

        {/* Other fields grid */}
        <div className="grid grid-cols-2 gap-3">
          <div>
            <p className={`text-xs font-medium mb-1 flex items-center gap-1 ${muted}`}><Calendar className="w-3 h-3" /> Due Date</p>
            <div className="flex items-center gap-1.5 flex-wrap">
              <p className="text-xs font-medium">{task.due_date ? new Date(task.due_date).toLocaleDateString('en-ZA') : '—'}</p>
              {(() => {
                const extCount = comments.filter(c => c.comment_type === 'due_extension').length
                return extCount > 0 ? (
                  <span className="text-xs px-1.5 py-0.5 rounded-full bg-orange-100 text-orange-600 font-medium">
                    Extended {extCount}×
                  </span>
                ) : null
              })()}
            </div>
          </div>
          <div>
            <p className={`text-xs font-medium mb-1 flex items-center gap-1 ${muted}`}><Flag className="w-3 h-3" /> Priority</p>
            <div className="flex gap-1">
              {[
                { key: 'low',    label: 'Low',    active: 'bg-green-100 text-green-700 border-green-300',    inactive: isDarkMode ? 'border-slate-600 text-slate-400 hover:border-green-400'    : 'border-gray-200 text-gray-400 hover:border-green-400' },
                { key: 'medium', label: 'Med',    active: 'bg-yellow-100 text-yellow-700 border-yellow-300', inactive: isDarkMode ? 'border-slate-600 text-slate-400 hover:border-yellow-400' : 'border-gray-200 text-gray-400 hover:border-yellow-400' },
                { key: 'high',   label: 'High',   active: 'bg-red-100 text-red-700 border-red-300',          inactive: isDarkMode ? 'border-slate-600 text-slate-400 hover:border-red-400'    : 'border-gray-200 text-gray-400 hover:border-red-400' },
              ].map(({ key, label, active, inactive }) => (
                <button
                  key={key}
                  onClick={() => handlePriorityChange(key)}
                  className={`px-2 py-0.5 rounded text-xs font-medium border transition ${
                    task.priority === key ? active : inactive
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>
          <div>
            <p className={`text-xs font-medium mb-1 ${muted}`}>Policy</p>
            <p className="text-xs font-medium">{task.policy_number ?? '—'}</p>
          </div>
          {task.claim_amount > 0 && (
            <div>
              <p className={`text-xs font-medium mb-1 ${muted}`}>Claim Amount</p>
              <p className="text-xs font-medium">R{(task.claim_amount ?? 0).toLocaleString('en-ZA')}</p>
            </div>
          )}
          <div>
            <p className={`text-xs font-medium mb-1 ${muted}`}>Created</p>
            <p className="text-xs font-medium">{new Date(task.created_at).toLocaleDateString('en-ZA')}</p>
          </div>
        </div>

        {/* Completion info */}
        {isCompleted && task.completed_at && (
          <div className={`p-3 rounded-lg border border-green-200 ${isDarkMode ? 'bg-green-900/20' : 'bg-green-50'}`}>
            <p className="text-xs font-semibold text-green-700 mb-1 flex items-center gap-1">
              <CheckCircle className="w-3.5 h-3.5" /> Completed {new Date(task.completed_at).toLocaleDateString('en-ZA')}
              {task.completed_by_advisor?.full_name && ` by ${task.completed_by_advisor.full_name}`}
            </p>
            {task.completion_notes && <p className="text-xs text-green-800">{task.completion_notes}</p>}
          </div>
        )}

        {/* Reopened info */}
        {task.reopened_reason && (
          <div className={`p-3 rounded-lg border border-yellow-200 ${isDarkMode ? 'bg-yellow-900/20' : 'bg-yellow-50'}`}>
            <p className="text-xs font-semibold text-yellow-700 mb-1 flex items-center gap-1">
              <RotateCcw className="w-3.5 h-3.5" /> Reopened
            </p>
            <p className="text-xs text-yellow-800">{task.reopened_reason}</p>
          </div>
        )}

        {/* Description */}
        {task.description && (
          <div>
            <p className={`text-xs font-semibold mb-1 ${muted}`}>Details</p>
            <pre className={`text-xs leading-relaxed p-3 rounded-lg border whitespace-pre-wrap font-sans ${cardBg}`}>
              {task.description}
            </pre>
          </div>
        )}

        {/* Attachments */}
        <div>
          <p className={`text-xs font-semibold mb-2 ${muted}`}>Attachments ({attachments.length})</p>
          {attachments.length === 0
            ? <p className={`text-xs ${muted}`}>No attachments.</p>
            : <div className="space-y-1.5">
                {attachments.map(att => (
                  <div key={att.id} className={`flex items-center justify-between p-2 rounded border ${cardBg}`}>
                    <div className="flex items-center gap-2 min-w-0">
                      <FileText className={`w-3.5 h-3.5 flex-shrink-0 ${muted}`} />
                      <p className="text-xs truncate">{att.file_name}</p>
                    </div>
                    <a href={att.file_url} target="_blank" rel="noopener noreferrer"
                      className="flex items-center gap-1 px-2 py-1 bg-sfp-orange text-white rounded text-xs hover:opacity-90 flex-shrink-0 ml-2">
                      <Download className="w-3 h-3" /> Download
                    </a>
                  </div>
                ))}
              </div>
          }
        </div>

        {/* Reassignment history */}
        {reassignments.length > 0 && (
          <div>
            <button onClick={() => setShowHistory(v => !v)}
              className={`flex items-center gap-1 text-xs font-semibold w-full text-left mb-1 ${muted} hover:text-sfp-orange transition`}>
              {showHistory ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
              Reassignment History ({reassignments.length})
            </button>
            {showHistory && (
              <div className="space-y-1.5">
                {reassignments.map(r => (
                  <div key={r.id} className={`p-2 rounded border text-xs ${cardBg}`}>
                    <p className="font-medium">{r.from_advisor?.full_name ?? '?'} → {r.to_advisor?.full_name ?? '?'}</p>
                    <p className={muted}>By {r.by_advisor?.full_name ?? '?'} · {new Date(r.reassigned_at).toLocaleDateString('en-ZA')}</p>
                    {r.reason && <p className="italic mt-0.5">{r.reason}</p>}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Comments / Activity / Subtasks tabs */}
        <div>
          <div className={`flex border-b mb-3 ${border}`}>
            {['comments', 'subtasks', 'checklist', 'activity'].map(tab => (
              <button key={tab} onClick={() => setActiveTab(tab)}
                className={`px-3 py-1.5 text-xs font-semibold capitalize transition ${
                  activeTab === tab
                    ? 'border-b-2 border-sfp-orange text-sfp-orange'
                    : `${muted} hover:text-gray-700 dark:hover:text-slate-300`
                }`}>
                {tab === 'comments' ? `Comments (${comments.length})` :
                 tab === 'subtasks' ? `Subtasks (${fullSubtasks.length})` :
                 tab === 'checklist' ? `Checklist (${subtasks.length})` :
                 'All Activity'}
              </button>
            ))}
          </div>

          {activeTab === 'comments' && (
            <div className="space-y-3">
              {comments.length === 0
                ? <p className={`text-xs ${muted}`}>No comments yet. Be the first to respond.</p>
                : comments.map(c => {
                    // ── Due date extension — render as timeline card, not bubble ──
                    if (c.comment_type === 'due_extension') {
                      let data = {}
                      try { data = JSON.parse(c.body) } catch {
                        data = {}
                      }
                      return (
                        <div key={c.id} className={`flex items-start gap-2 py-2 px-3 rounded-lg border-l-4 border-orange-400 ${isDarkMode ? 'bg-slate-800/60' : 'bg-orange-50'}`}>
                          <CalendarClock className="w-4 h-4 text-orange-500 flex-shrink-0 mt-0.5" />
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="text-xs font-semibold text-orange-600">Due date extended</span>
                              <span className={`text-xs ${muted}`}>by {c.advisors?.full_name ?? 'Unknown'} · {new Date(c.created_at).toLocaleString('en-ZA')}</span>
                            </div>
                            <p className={`text-xs mt-0.5 ${isDarkMode ? 'text-slate-300' : 'text-gray-700'}`}>
                              {data.old_date ? new Date(data.old_date).toLocaleDateString('en-ZA') : '—'}
                              {' → '}
                              <strong>{data.new_date ? new Date(data.new_date).toLocaleDateString('en-ZA') : '—'}</strong>
                            </p>
                            {data.reason && (
                              <p className={`text-xs mt-0.5 italic ${muted}`}>"{data.reason}"</p>
                            )}
                          </div>
                        </div>
                      )
                    }

                    return (
                    <div key={c.id} className="flex gap-2">
                      <AdvisorAvatar avatarUrl={c.advisors?.avatar_url ?? null} fullName={c.advisors?.full_name ?? ''} size="sm" />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-baseline gap-2">
                          <span className="text-xs font-semibold">{c.advisors?.full_name ?? 'Unknown'}</span>
                          <span className={`text-xs ${muted}`}>{new Date(c.created_at).toLocaleString('en-ZA')}</span>
                        </div>
                        {c.comment_type === 'voice' && c.media_url ? (
                          <VoicePlayer src={c.media_url} isDarkMode={isDarkMode} />
                        ) : c.comment_type === 'file' && c.media_url ? (
                          c.media_mime?.startsWith('image/') ? (
                            /* ── Inline image preview ── */
                            <div className={`mt-1 rounded-lg overflow-hidden border ${isDarkMode ? 'border-slate-700' : 'border-gray-200'}`}>
                              <img
                                src={c.media_url}
                                alt={c.media_name ?? 'image'}
                                className="max-w-full max-h-48 object-contain cursor-zoom-in block"
                                onClick={() => setExpandedMedia({ url: c.media_url, mime: c.media_mime, name: c.media_name ?? 'image' })}
                              />
                              <div className={`flex items-center justify-between px-2 py-1 text-xs ${isDarkMode ? 'bg-slate-800 text-slate-400' : 'bg-gray-50 text-gray-500'}`}>
                                <span className="truncate">{c.media_name}</span>
                                <a href={c.media_url} download target="_blank" rel="noopener noreferrer"
                                  className="flex items-center gap-1 ml-2 flex-shrink-0 hover:text-sfp-orange">
                                  <Download className="w-3 h-3" />
                                </a>
                              </div>
                            </div>
                          ) : c.media_mime === 'application/pdf' ? (
                            /* ── PDF viewer ── */
                            <div className={`mt-1 rounded-lg border overflow-hidden ${isDarkMode ? 'border-slate-700' : 'border-gray-200'}`}>
                              <div className={`flex items-center justify-between px-2 py-1.5 text-xs ${isDarkMode ? 'bg-slate-800 text-slate-300' : 'bg-gray-50 text-gray-700'}`}>
                                <div className="flex items-center gap-1.5 min-w-0">
                                  <FileText className="w-3.5 h-3.5 flex-shrink-0 text-red-500" />
                                  <span className="truncate">{c.media_name ?? 'document.pdf'}</span>
                                  {c.media_size && <span className={`flex-shrink-0 ${muted}`}>({(c.media_size / 1024).toFixed(0)} KB)</span>}
                                </div>
                                <div className="flex items-center gap-2 ml-2 flex-shrink-0">
                                  <button
                                    onClick={() => setExpandedMedia({ url: c.media_url, mime: c.media_mime, name: c.media_name ?? 'document.pdf' })}
                                    className="flex items-center gap-1 px-2 py-0.5 bg-sfp-orange text-white rounded hover:opacity-90">
                                    View
                                  </button>
                                  <a href={c.media_url} download target="_blank" rel="noopener noreferrer"
                                    className={`hover:text-sfp-orange ${muted}`}>
                                    <Download className="w-3.5 h-3.5" />
                                  </a>
                                </div>
                              </div>
                            </div>
                          ) : (
                            /* ── Generic file download card ── */
                            <div className={`mt-1 flex items-center justify-between p-2 rounded border ${cardBg}`}>
                              <div className="flex items-center gap-1.5 min-w-0">
                                <FileText className={`w-3.5 h-3.5 flex-shrink-0 ${muted}`} />
                                <span className="text-xs truncate">{c.media_name ?? c.body}</span>
                                {c.media_size && <span className={`text-xs flex-shrink-0 ${muted}`}>({(c.media_size / 1024).toFixed(0)} KB)</span>}
                              </div>
                              <a href={c.media_url} target="_blank" rel="noopener noreferrer"
                                className="flex items-center gap-1 px-2 py-0.5 bg-sfp-orange text-white rounded text-xs hover:opacity-90 flex-shrink-0 ml-2">
                                <Download className="w-3 h-3" /> Get
                              </a>
                            </div>
                          )
                        ) : (
                          <p className={`text-xs mt-0.5 ${isDarkMode ? 'text-slate-300' : 'text-gray-700'}`}>{c.body}</p>
                        )}

                        {/* Reaction pills */}
                        <div className="flex items-center gap-1 mt-1 flex-wrap">
                          {Object.entries(
                            (reactionsMap[c.id] ?? []).reduce((acc, r) => {
                              if (!acc[r.emoji]) acc[r.emoji] = []
                              acc[r.emoji].push(r.advisor_id)
                              return acc
                            }, {})
                          ).map(([emoji, advisorIds]) => (
                            <button key={emoji}
                              onClick={() => handleToggleReaction(c.id, emoji)}
                              className={`flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-xs border transition ${
                                advisorIds.includes(currentAdvisor?.id)
                                  ? 'bg-sfp-orange/20 border-sfp-orange text-sfp-orange'
                                  : (isDarkMode ? 'border-slate-600 hover:border-slate-400' : 'border-gray-200 hover:border-gray-300')
                              }`}
                            >
                              {emoji}{advisorIds.length > 1 && <span className="ml-0.5">{advisorIds.length}</span>}
                            </button>
                          ))}
                          {/* Add reaction button */}
                          <button
                            onClick={() => setReactionPickerFor(reactionPickerFor === c.id ? null : c.id)}
                            className={`px-1.5 py-0.5 text-xs rounded-full border border-dashed transition ${
                              isDarkMode
                                ? 'border-slate-600 text-slate-500 hover:border-sfp-orange hover:text-sfp-orange'
                                : 'border-gray-300 text-gray-400 hover:border-sfp-orange hover:text-sfp-orange'
                            }`}
                          >+ 😊</button>
                        </div>

                        {/* Inline emoji picker for reactions */}
                        {reactionPickerFor === c.id && (
                          <div className={`mt-1 p-1.5 rounded-lg border shadow-md grid grid-cols-10 gap-0.5 ${
                            isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-gray-200'
                          }`}>
                            {COMMON_EMOJI.map(e => (
                              <button key={e}
                                onMouseDown={() => { handleToggleReaction(c.id, e); setReactionPickerFor(null) }}
                                className="text-sm hover:scale-125 transition-transform p-0.5 rounded hover:bg-gray-100 dark:hover:bg-slate-700"
                              >{e}</button>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  )
                  })}
              <div ref={commentsEndRef} />
            </div>
          )}

          {activeTab === 'subtasks' && (
            <div className="space-y-2">
              {/* Header row */}
              <div className="flex items-center justify-between">
                <span className={`text-xs font-semibold ${isDarkMode ? 'text-slate-300' : 'text-gray-600'}`}>
                  {fullSubtasks.length} subtask{fullSubtasks.length !== 1 ? 's' : ''}
                </span>
                <button
                  onClick={() => setCreatingSubtask(true)}
                  className="text-xs text-sfp-orange hover:underline font-medium"
                >
                  + Create Subtask
                </button>
              </div>

              {/* Subtask rows — clickable, open full detail panel */}
              {fullSubtasks.map(st => (
                <div
                  key={st.id}
                  onClick={() => onOpenTask?.(st.id)}
                  className={`flex items-center gap-3 px-3 py-2 rounded-lg border cursor-pointer transition ${
                    isDarkMode
                      ? 'border-slate-700 hover:bg-slate-800'
                      : 'border-gray-200 hover:bg-orange-50'
                  }`}
                >
                  <span className={`text-xs px-2 py-0.5 rounded-full font-medium flex-shrink-0 ${STATUS_COLORS[st.status] ?? STATUS_COLORS.open}`}>
                    {st.status.replace('_', ' ')}
                  </span>
                  <span className={`flex-1 text-sm font-medium truncate ${isDarkMode ? 'text-slate-200' : 'text-gray-800'}`}>
                    {st.title}
                  </span>
                  {st.advisors && (
                    <AdvisorAvatar size="sm" fullName={st.advisors.full_name} avatarUrl={st.advisors.avatar_url ?? null} />
                  )}
                  {st.due_date && (
                    <span className={`text-xs flex-shrink-0 ${isDarkMode ? 'text-slate-400' : 'text-gray-400'}`}>
                      {new Date(st.due_date).toLocaleDateString('en-ZA')}
                    </span>
                  )}
                  <ChevronRight className={`w-3 h-3 flex-shrink-0 ${isDarkMode ? 'text-slate-500' : 'text-gray-400'}`} />
                </div>
              ))}

              {fullSubtasks.length === 0 && (
                <p className={`text-xs ${muted}`}>No subtasks yet. Click "+ Create Subtask" to add one.</p>
              )}
            </div>
          )}

          {activeTab === 'checklist' && (
            <div className="space-y-3">
              {/* Progress bar */}
              {subtasks.length > 0 && (
                <div className="space-y-1">
                  <div className="flex justify-between items-center text-xs">
                    <span className="font-medium">Progress</span>
                    <span className={muted}>{subtasks.filter(s => s.completed).length} / {subtasks.length}</span>
                  </div>
                  <div className={`h-2 rounded-full ${isDarkMode ? 'bg-slate-700' : 'bg-gray-200'}`}>
                    <div
                      className="h-full bg-green-500 rounded-full transition-all"
                      style={{
                        width: `${subtasks.length > 0 ? (subtasks.filter(s => s.completed).length / subtasks.length) * 100 : 0}%`,
                      }}
                    />
                  </div>
                </div>
              )}

              {/* Checklist items */}
              {subtasks.length === 0 && !addingSubtask ? (
                <p className={`text-xs ${muted}`}>No checklist items yet.</p>
              ) : (
                <div className="space-y-2">
                  {subtasks.map(st => (
                    <div key={st.id} className={`flex items-center gap-2 p-2 rounded border ${
                      isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-gray-50 border-gray-200'
                    }`}>
                      <input
                        type="checkbox"
                        checked={st.completed}
                        onChange={() => toggleSubtask(st.id, st.completed)}
                        className="cursor-pointer"
                      />
                      <div className="flex-1 min-w-0">
                        <p className={`text-xs font-medium ${st.completed ? `line-through ${muted}` : ''}`}>
                          {st.title}
                        </p>
                        <div className="flex items-center gap-2 mt-1 flex-wrap">
                          {st.advisors && (
                            <div className="flex items-center gap-1">
                              <AdvisorAvatar avatarUrl={st.advisors.avatar_url ?? null} fullName={st.advisors.full_name} size="sm" />
                              <span className="text-xs">{st.advisors.full_name}</span>
                            </div>
                          )}
                          {st.due_date && (
                            <span className={`text-xs px-2 py-0.5 rounded ${
                              new Date(st.due_date) < new Date() && !st.completed
                                ? 'bg-red-100 text-red-700'
                                : 'bg-gray-200 text-gray-700'
                            }`}>
                              {new Date(st.due_date).toLocaleDateString('en-ZA')}
                            </span>
                          )}
                        </div>
                      </div>
                      <button
                        onClick={() => deleteSubtask(st.id)}
                        className="text-gray-400 hover:text-red-500 transition"
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {/* Add checklist item form */}
              {addingSubtask ? (
                <div className={`p-3 rounded border space-y-2 ${
                  isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-gray-50 border-gray-200'
                }`}>
                  <input
                    type="text"
                    value={newSubtask.title}
                    onChange={(e) => setNewSubtask(prev => ({ ...prev, title: e.target.value }))}
                    placeholder="Checklist item title..."
                    className={`w-full text-xs rounded border px-2 py-1.5 ${inputCls}`}
                    autoFocus
                  />
                  <select
                    value={newSubtask.assigned_to}
                    onChange={(e) => setNewSubtask(prev => ({ ...prev, assigned_to: e.target.value }))}
                    className={`w-full text-xs rounded border px-2 py-1.5 ${inputCls}`}
                  >
                    <option value="">— Assign to (optional) —</option>
                    {advisors.map(a => (
                      <option key={a.id} value={a.id}>{a.full_name}</option>
                    ))}
                  </select>
                  <input
                    type="date"
                    value={newSubtask.due_date}
                    onChange={(e) => setNewSubtask(prev => ({ ...prev, due_date: e.target.value }))}
                    className={`w-full text-xs rounded border px-2 py-1.5 ${inputCls}`}
                  />
                  <div className="flex gap-2">
                    <button
                      onClick={createSubtask}
                      disabled={!newSubtask.title.trim()}
                      className="px-3 py-1 bg-green-600 text-white rounded text-xs hover:bg-green-700 disabled:opacity-50"
                    >
                      Save
                    </button>
                    <button
                      onClick={() => {
                        setAddingSubtask(false)
                        setNewSubtask({ title: '', assigned_to: '', due_date: '' })
                      }}
                      className={`px-3 py-1 rounded text-xs ${cancelBtn}`}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <button
                  onClick={() => setAddingSubtask(true)}
                  className="text-xs font-medium text-sfp-orange hover:text-orange-700"
                >
                  + Add item
                </button>
              )}
            </div>
          )}

          {activeTab === 'activity' && (
            <div className="space-y-2">
              {activity.map((a, i) => (
                <div key={i} className="flex items-start gap-2">
                  <div className={`w-1.5 h-1.5 rounded-full mt-1.5 flex-shrink-0 ${
                    a.type === 'completed' ? 'bg-green-500' :
                    a.type === 'reopened'  ? 'bg-yellow-500' :
                    a.type === 'reassigned'? 'bg-blue-500' :
                    a.type === 'extended'  ? 'bg-orange-500' : 'bg-gray-400'
                  }`} />
                  <div>
                    <p className={`text-xs ${isDarkMode ? 'text-slate-300' : 'text-gray-700'}`}>{a.label}</p>
                    <p className={`text-xs ${muted}`}>{new Date(a.ts).toLocaleString('en-ZA')}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* ── Comment input (sticky bottom) ── */}
      <div className={`flex-shrink-0 border-t px-4 py-3 ${border}`}>
        {/* Emoji picker */}
        {emojiOpen && (
          <div className={`mb-2 rounded-lg border shadow-lg p-2 ${isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-gray-200'}`}>
            <div className="grid grid-cols-10 gap-1">
              {COMMON_EMOJI.map(e => (
                <button key={e} type="button" onMouseDown={() => handleEmojiSelect(e)}
                  className="text-base hover:scale-125 transition-transform leading-none p-0.5 rounded hover:bg-gray-100 dark:hover:bg-slate-700">
                  {e}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* @mention dropdown */}
        {mentionOpen && filteredMentions.length > 0 && (
          <div className={`mb-2 rounded-lg border shadow-lg max-h-36 overflow-y-auto ${
            isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-gray-200'
          }`}>
            {filteredMentions.map(a => (
              <button
                key={a.id}
                type="button"
                onMouseDown={(e) => { e.preventDefault(); insertMention(a) }}
                className={`w-full text-left px-2.5 py-2 text-xs flex items-center gap-2 transition hover:bg-sfp-orange/10 ${
                  isDarkMode ? 'text-slate-200' : 'text-gray-700'
                }`}
              >
                <AdvisorAvatar avatarUrl={a.avatar_url ?? null} fullName={a.full_name} size="sm" />
                <span className="font-medium">{a.full_name}</span>
              </button>
            ))}
          </div>
        )}

        {/* Upload/voice status */}
        {(uploadingFile || uploadingVoice) && (
          <p className={`text-xs mb-1.5 ${muted}`}>
            {uploadingFile ? '📎 Uploading file…' : '🎙 Saving voice note…'}
          </p>
        )}
        {voiceError && (
          <p className="text-xs mb-1.5 text-red-500 font-medium">
            🎙 {voiceError}
          </p>
        )}

        {/* Hidden file input */}
        <input ref={chatFileRef} type="file" name="chat-file" hidden onChange={handleChatFileSelect} />

        <form onSubmit={handleAddComment} className="flex items-center gap-1.5">
          <AdvisorAvatar avatarUrl={currentAdvisor?.avatar_url ?? null} fullName={currentAdvisor?.full_name ?? ''} size="sm" />
          <input
            ref={commentInputRef}
            name="comment"
            value={commentBody}
            onChange={handleCommentChange}
            onBlur={() => setTimeout(() => { setMentionOpen(false); setEmojiOpen(false) }, 150)}
            placeholder="Reply… type @ to mention"
            className={`flex-1 text-xs rounded-full border px-3 py-1.5 focus:outline-none focus:ring-1 focus:ring-sfp-orange ${inputCls}`}
          />
          {/* Emoji button */}
          <button type="button" onClick={() => setEmojiOpen(v => !v)}
            title="Emoji"
            className={`p-1.5 rounded-full transition text-base leading-none ${
              emojiOpen ? 'bg-sfp-orange/20' : (isDarkMode ? 'text-slate-400 hover:bg-slate-700' : 'text-gray-400 hover:bg-gray-100')
            }`}>
            😊
          </button>
          {/* File attach button */}
          <button type="button" onClick={() => chatFileRef.current?.click()}
            disabled={uploadingFile}
            title="Attach file"
            className={`p-1.5 rounded-full transition ${isDarkMode ? 'text-slate-400 hover:bg-slate-700' : 'text-gray-400 hover:bg-gray-100'} disabled:opacity-40`}>
            <Paperclip className="w-3.5 h-3.5" />
          </button>
          {/* Voice note button */}
          {voiceSupported ? (
            <button type="button" onClick={handleVoiceToggle}
              disabled={uploadingVoice}
              title={recording ? 'Stop recording' : 'Record voice note'}
              className={`p-1.5 rounded-full transition disabled:opacity-40 ${
                recording
                  ? 'bg-red-500 text-white animate-pulse'
                  : (isDarkMode ? 'text-slate-400 hover:bg-slate-700' : 'text-gray-400 hover:bg-gray-100')
              }`}>
              {recording ? <MicOff className="w-3.5 h-3.5" /> : <Mic className="w-3.5 h-3.5" />}
            </button>
          ) : null}
          {/* Send button */}
          <button type="submit" disabled={!commentBody.trim() || sendingComment}
            className="p-1.5 bg-sfp-orange text-white rounded-full hover:opacity-90 transition disabled:opacity-40">
            <Send className="w-3.5 h-3.5" />
          </button>
        </form>
      </div>

      {/* ── Media lightbox ── */}
      {expandedMedia && (
        <div
          className="fixed inset-0 z-50 bg-black/85 flex items-center justify-center p-4"
          onClick={() => setExpandedMedia(null)}
        >
          <div
            className="relative bg-white dark:bg-slate-900 rounded-xl overflow-hidden shadow-2xl max-w-5xl w-full max-h-[90vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className={`flex items-center justify-between px-4 py-2.5 border-b flex-shrink-0 ${isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-gray-50 border-gray-200'}`}>
              <span className={`text-sm font-medium truncate ${isDarkMode ? 'text-slate-200' : 'text-gray-700'}`}>{expandedMedia.name}</span>
              <div className="flex items-center gap-3 ml-4 flex-shrink-0">
                <a href={expandedMedia.url} download target="_blank" rel="noopener noreferrer"
                  className="flex items-center gap-1.5 px-3 py-1 bg-sfp-orange text-white rounded-lg text-xs hover:opacity-90">
                  <Download className="w-3.5 h-3.5" /> Download
                </a>
                <button onClick={() => setExpandedMedia(null)}
                  className={`p-1 rounded hover:bg-gray-200 dark:hover:bg-slate-700 ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}>
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>
            {/* Content */}
            <div className="flex-1 overflow-auto flex items-center justify-center bg-black/10 dark:bg-black/40 min-h-0">
              {expandedMedia.mime?.startsWith('image/') ? (
                <img
                  src={expandedMedia.url}
                  alt={expandedMedia.name}
                  className="max-w-full max-h-full object-contain p-4"
                />
              ) : expandedMedia.mime === 'application/pdf' ? (
                <iframe
                  src={expandedMedia.url}
                  title={expandedMedia.name}
                  className="w-full h-full min-h-[70vh]"
                  style={{ border: 'none' }}
                />
              ) : null}
            </div>
          </div>
        </div>
      )}

      {/* ── Create Subtask Modal ── */}
      {creatingSubtask && (
        <CreateTaskModal
          isOpen={true}
          isDarkMode={isDarkMode}
          parentTaskId={taskId}
          parentTaskTitle={task?.title}
          initialClientId={task?.crm_client_id ?? null}
          initialClientName={task?.client_name ?? null}
          initialPolicyId={task?.policy_id ?? null}
          initialPolicyNumber={task?.policy_number ?? null}
          onClose={() => setCreatingSubtask(false)}
          onCreated={() => {
            setCreatingSubtask(false)
            supabase.from('sfp_tasks')
              .select('id, title, status, priority, due_date, advisor_id, advisors!advisor_id(id, full_name, avatar_url)')
              .eq('parent_task_id', taskId)
              .is('deleted_at', null)
              .order('created_at')
              .then(({ data }) => setFullSubtasks(data ?? []))
          }}
        />
      )}
    </div>
  )
}
